import { differenceInDays, getYear } from 'date-fns'
import { PeriodValidationError } from '../types'

/**
 * Verifica si una fecha es futura (posterior a hoy).
 */
export const isFutureDate = (date: Date): boolean => {
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  return date > today
}

/**
 * Devuelve la cantidad de semanas ISO en un año (52 o 53).
 * Regla ISO: un año tiene 53 semanas si el 1 de enero es jueves,
 * o si el 1 de enero es miércoles y es año bisiesto.
 */
export const getISOWeeksInYear = (year: number): number => {
  const jan1 = new Date(year, 0, 1)
  const dayOfWeek = jan1.getDay() // 0=domingo, 1=lunes, ..., 6=sábado

  // jueves = 4
  if (dayOfWeek === 4) return 53
  // miércoles = 3 en año bisiesto
  if (dayOfWeek === 3 && isLeapYear(year)) return 53
  return 52
}

const isLeapYear = (year: number): boolean => {
  return (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0
}

/**
 * Valida un rango de fechas.
 * Retorna null si es válido, o un error traducible si no.
 */
export const validateDateRange = (
  from: Date | null,
  to: Date | null
): PeriodValidationError | null => {
  // Si solo hay una fecha, no hay error de rango todavía
  if (!from || !to) {
    // Pero si hay una sola fecha, no hay error (el usuario puede estar en proceso)
    return null
  }

  if (isFutureDate(from)) {
    return 'dashboard.storePerformance.filter.validation.futureDate'
  }

  if (isFutureDate(to)) {
    return 'dashboard.storePerformance.filter.validation.futureDate'
  }

  const fromYear = getYear(from)
  const toYear = getYear(to)

  if (fromYear !== toYear) {
    return 'dashboard.storePerformance.filter.validation.differentYear'
  }

  if (to < from) {
    return 'dashboard.storePerformance.filter.validation.toBeforeFrom'
  }

  const diff = differenceInDays(to, from)
  if (diff > 365) {
    return 'dashboard.storePerformance.filter.validation.max365Days'
  }

  return null
}

/**
 * Valida un conjunto de semanas para un año dado.
 * Retorna null si es válido, o un error traducible si no.
 */
export const validateWeeks = (
  weeks: string[],
  year: number
): PeriodValidationError | null => {
  if (weeks.length === 0) return null

  const maxWeeks = getISOWeeksInYear(year)
  if (weeks.length > maxWeeks) {
    return 'dashboard.storePerformance.filter.validation.maxWeeks'
  }

  return null
}

/**
 * Obtiene el año de una fecha.
 */
export const getYearFromDate = (date: Date): number => {
  return getYear(date)
}

/**
 * Verifica si un valor es una fecha válida (no null, no undefined, no NaN).
 */
export const isValidDate = (date: Date | null | undefined): date is Date => {
  return date instanceof Date && !isNaN(date.getTime())
}
